
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Head from 'next/head';
import { useState, useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';
import FormattedDate from '../components/FormattedDate';
import { cachedDataWrapper } from '../lib/dataFetcher';
import { SITE_URL } from '../lib/site';
// Minimal, iframe-embeddable widget showing the live CPH security wait.
// Rendered without site chrome (see _app.js) so third-party sites can embed it.
// noindex: it's a fragment, not a page that should compete in search.
//
// Three layouts, selected via ?variant=:
//   card    (default) - full card with label, big number and timestamp
//   compact          - smaller card, no timestamp
//   line             - single horizontal row (banner-style)
async function getStaticProps() {
    try {
        const data = await cachedDataWrapper();
        return {
            props: {
                initialData: {
                    waitTime: data.waitTime ?? null,
                    lastUpdated: data.lastUpdated ?? null,
                    timezone: data.timezone ?? '',
                    error: data.error ?? null
                }
            },
            revalidate: 60
        };
    }
    catch (error) {
        console.error('Error building widget:', error);
        return { props: { initialData: { error: true } }, revalidate: 60 };
    }
}
const VARIANTS = ['card', 'compact', 'line'];
function Widget({ initialData }) {
    const { t, lang } = useTranslation('widget');
    const [data, setData] = useState(initialData);
    // Render the default ('card') on the server and first client paint to avoid
    // hydration mismatch, then pick up ?variant= from the URL once mounted.
    const [variant, setVariant] = useState('card');
    useEffect(() => {
        const v = new URLSearchParams(window.location.search).get('variant');
        if (VARIANTS.includes(v))
            setVariant(v);
    }, []);
    useEffect(() => {
        // Report where the widget is embedded. In an iframe, document.referrer is
        // the parent page and ancestorOrigins[0] the parent origin - so umami's
        // events view lists exactly which sites embed the widget.
        if (typeof umami === 'undefined')
            return;
        umami.track('widget-embed', {
            parent: document.referrer || 'direct',
            ancestor: window.location.ancestorOrigins?.[0] || 'none'
        });
    }, []);
    useEffect(() => {
        // Poll the slim, CDN-cacheable widget endpoint (not /api/fetchData),
        // and only while the tab is visible. Most embeds sit in background
        // tabs, so pausing there cuts requests dramatically at scale.
        let id;
        const fetchData = async () => {
            try {
                const res = await fetch('/api/widget');
                if (!res.ok)
                    throw new Error(res.statusText);
                const json = await res.json();
                if (!json?.error)
                    setData(json);
            }
            catch {
                // keep last good data
            }
        };
        const start = () => {
            if (id)
                return;
            fetchData();
            id = setInterval(fetchData, 1000 * 60 * 5);
        };
        const stop = () => {
            clearInterval(id);
            id = undefined;
        };
        const onVisibility = () => {
            if (document.hidden)
                stop();
            else
                start();
        };
        if (!document.hidden)
            start();
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            stop();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);
    const waitTime = data?.waitTime;
    const hasValue = waitTime && waitTime !== 'error' && !data?.error;
    const value = hasValue ? String(waitTime).replace(' min', '') : null;
    const href = lang === 'en' ? `${SITE_URL}/en` : `${SITE_URL}/`;
    const linkProps = {
        href,
        target: '_blank',
        rel: 'noopener noreferrer nofollow'
    };
    let body;
    if (variant === 'line') {
        // Single horizontal row, banner-style.
        body = (<a {...linkProps} className="flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-800 no-underline shadow-sm">
                <span className="text-sm font-semibold text-gray-600 whitespace-nowrap">
                    {t('heading')}
                </span>
                {hasValue ? (<span className="text-xl font-bold text-[var(--primary)] whitespace-nowrap">
                        {value}{' '}
                        <span className="text-sm font-semibold">
                            {t('common:minutes')}
                        </span>
                    </span>) : (<span className="text-sm text-gray-500">{t('error')}</span>)}
                <span className="ml-auto text-[11px] text-gray-400 whitespace-nowrap">
                    {t('attribution')}
                </span>
            </a>);
    }
    else if (variant === 'compact') {
        // Smaller card, no timestamp.
        body = (<a {...linkProps} className="block w-full max-w-[240px] rounded-lg border border-gray-200 bg-white p-3 text-center text-gray-800 no-underline shadow-sm">
                <div className="text-xs font-semibold text-gray-600">
                    {t('heading')}
                </div>
                {hasValue ? (<div className="my-1 text-3xl font-bold text-[var(--primary)]">
                        {value}{' '}
                        <span className="text-base">{t('common:minutes')}</span>
                    </div>) : (<div className="my-2 text-sm text-gray-500">
                        {t('error')}
                    </div>)}
                <div className="text-[11px] text-gray-400">
                    {t('attribution')}
                </div>
            </a>);
    }
    else {
        // Default full card.
        body = (<a {...linkProps} className="block w-full max-w-xs rounded-lg border border-gray-200 shadow-sm p-4 text-center text-gray-800 no-underline bg-white">
                <div className="text-sm font-semibold text-gray-600">
                    {t('heading')}
                </div>
                <div className="text-xs text-gray-500">{t('sub')}</div>

                {hasValue ? (<>
                        <div className="text-5xl font-bold my-2 text-[var(--primary)]">
                            {value}{' '}
                            <span className="text-2xl">
                                {t('common:minutes')}
                            </span>
                        </div>
                        <div className="text-xs text-gray-500">
                            {t('common:last_updated')}:{' '}
                            {data?.lastUpdated ? (<FormattedDate date={data.lastUpdated} locale={lang}/>) : ('-')}
                        </div>
                    </>) : (<div className="text-base my-3 text-gray-500">
                        {t('error')}
                    </div>)}

                <div className="mt-2 text-[11px] text-gray-400">
                    {t('attribution')}
                </div>
            </a>);
    }
    return (<>
            <Head>
                <meta name="robots" content="noindex"/>
                <title>{t('heading')} - CPH</title>
            </Head>
            <div className="min-h-screen flex items-center justify-center p-3 bg-white">
                {body}
            </div>
        </>);
}
export default Widget;

    async function __Next_Translate__getStaticProps__1a1723ec6e6__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/widget',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1723ec6e6__ as getStaticProps }
  